import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import { Settings, Save, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const SystemSettings = () => {
  const [savingKey, setSavingKey] = useState(null);

  const { data: settings, isLoading, refetch } = useQuery(
    'system-settings',
    adminAPI.getSettings,
    {
      select: (response) => response.data.data.settings
    }
  );

  const handleSaveSetting = async (key, value, description = '') => {
    setSavingKey(key);
    try {
      await adminAPI.updateSetting(key, { value, description });
      toast.success('Setting updated successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSavingKey(null);
    }
  };

  const SettingItem = ({ settingKey, title, description, type = 'text', options = [] }) => {
    const [value, setValue] = useState(settings?.[settingKey]?.value || '');
    const [isEditing, setIsEditing] = useState(false);

    React.useEffect(() => {
      setValue(settings?.[settingKey]?.value || '');
    }, [settings, settingKey]);

    const handleSave = () => {
      handleSaveSetting(settingKey, value, description);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setValue(settings?.[settingKey]?.value || '');
      setIsEditing(false);
    };

    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="text-sm font-medium text-gray-900">{title}</h4>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="mt-3">
            {type === 'select' ? (
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="input mb-3"
              >
                {options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : type === 'textarea' ? (
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="textarea mb-3"
                rows={3}
              />
            ) : type === 'number' ? (
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="input mb-3"
              />
            ) : (
              <input
                type={type}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="input mb-3"
              />
            )}
            
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={savingKey === settingKey}
                className="btn btn-primary btn-sm"
              >
                {savingKey === settingKey ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                className="btn btn-outline btn-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <div className="bg-gray-50 rounded-md p-2">
              <span className="text-sm text-gray-900">
                {value || <em className="text-gray-500">Not set</em>}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">System Settings</h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure system-wide settings and preferences
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-outline"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Settings Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        <SettingItem
          settingKey="site_name"
          title="Site Name"
          description="The name of your reseller panel"
          type="text"
        />

        <SettingItem
          settingKey="site_description"
          title="Site Description"
          description="A brief description of your service"
          type="textarea"
        />

        <SettingItem
          settingKey="contact_email"
          title="Contact Email"
          description="Primary contact email for support"
          type="email"
        />

        <SettingItem
          settingKey="default_currency"
          title="Default Currency"
          description="Currency used for pricing"
          type="select"
          options={[
            { value: 'USD', label: 'US Dollar (USD)' },
            { value: 'EUR', label: 'Euro (EUR)' },
            { value: 'GBP', label: 'British Pound (GBP)' },
            { value: 'CAD', label: 'Canadian Dollar (CAD)' }
          ]}
        />

        <SettingItem
          settingKey="order_prefix"
          title="Order Number Prefix"
          description="Prefix for order numbers (e.g., ORD)"
          type="text"
        />

        <SettingItem
          settingKey="max_orders_per_hour"
          title="Max Orders Per Hour"
          description="Rate limit for order creation"
          type="number"
        />

        <SettingItem
          settingKey="enable_email_notifications"
          title="Email Notifications"
          description="Send email notifications for orders"
          type="select"
          options={[
            { value: 'true', label: 'Enabled' },
            { value: 'false', label: 'Disabled' }
          ]}
        />

        <SettingItem
          settingKey="maintenance_mode"
          title="Maintenance Mode"
          description="Enable maintenance mode to disable new orders"
          type="select"
          options={[
            { value: 'false', label: 'Disabled' },
            { value: 'true', label: 'Enabled' }
          ]}
        />

        <SettingItem
          settingKey="api_timeout"
          title="API Timeout (seconds)"
          description="Timeout for external API calls"
          type="number"
        />

        <SettingItem
          settingKey="log_level"
          title="Log Level"
          description="System logging level"
          type="select"
          options={[
            { value: 'error', label: 'Error' },
            { value: 'warn', label: 'Warning' },
            { value: 'info', label: 'Info' },
            { value: 'debug', label: 'Debug' }
          ]}
        />

        <SettingItem
          settingKey="backup_retention_days"
          title="Backup Retention (days)"
          description="How long to keep database backups"
          type="number"
        />

        <SettingItem
          settingKey="session_timeout"
          title="Session Timeout (hours)"
          description="How long user sessions remain active"
          type="number"
        />
      </div>

      {/* System Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">System Information</h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Environment:</span>
              <span className="ml-2 font-medium">{process.env.NODE_ENV || 'development'}</span>
            </div>
            <div>
              <span className="text-gray-500">Version:</span>
              <span className="ml-2 font-medium">1.0.0</span>
            </div>
            <div>
              <span className="text-gray-500">Domain:</span>
              <span className="ml-2 font-medium">{window.location.hostname}</span>
            </div>
            <div>
              <span className="text-gray-500">Protocol:</span>
              <span className="ml-2 font-medium">{window.location.protocol}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <Settings className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Configuration Notice
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Changes to system settings take effect immediately. Some settings may require 
                a server restart to fully apply. Always test changes in a development environment first.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
